import { ResourcePlacement } from '../../../enums/enums';
import { FileObjectStatus, FileObjectType, type IFileObjectDTO } from '../../../models/fileObjects';
import { getDateOffset } from '../support/date-utils';

interface DevSurveyFixture {
  id: string;
  clientId: string;
  company: string;
  type: string;
  target: string;
  code: string;
}

// --- Content Fixtures ---

export const baseLearningTools: IFileObjectDTO[] = [
  {
    id: 'learning-tool-home-1',
    filename: 'executive-presence-guide.pdf',
    mimeType: 'application/pdf',
    type: FileObjectType.LearningTool,
    status: FileObjectStatus.Active,
    size: 1200000,
    azureUrl: 'https://example.com/executive-presence-guide',
    updatedAt: new Date(getDateOffset(-2, 8)),
    thumbnailUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80',
    title: 'Executive Presence Guide',
    placement: ResourcePlacement.Homepage,
    clientIds: ['client-dev-1'],
    cohortIds: ['dev-group-1']
  },
  {
    id: 'learning-tool-home-2',
    filename: 'coaching-reflection-canvas.pdf',
    mimeType: 'application/pdf',
    type: FileObjectType.LearningTool,
    status: FileObjectStatus.Active,
    size: 980000,
    azureUrl: 'https://example.com/coaching-reflection-canvas',
    updatedAt: new Date(getDateOffset(-4, 8)),
    thumbnailUrl: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=900&q=80',
    title: 'Coaching Reflection Canvas',
    placement: ResourcePlacement.Homepage,
    clientIds: ['client-dev-1'],
    cohortIds: ['dev-group-1']
  },
  {
    id: 'learning-tool-resource-1',
    filename: 'strategic-communication-toolkit.pdf',
    mimeType: 'application/pdf',
    type: FileObjectType.LearningTool,
    status: FileObjectStatus.Active,
    size: 1500000,
    azureUrl: 'https://example.com/strategic-communication-toolkit',
    updatedAt: new Date(getDateOffset(-6, 8)),
    thumbnailUrl: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=900&q=80',
    title: 'Strategic Communication Toolkit',
    placement: ResourcePlacement.ResourcesPage,
    clientIds: ['client-dev-1'],
    cohortIds: ['dev-group-1']
  }
];

// --- Survey Fixtures ---

export const baseSurveys: DevSurveyFixture[] = [
  {
    id: 'dev-survey-onboarding',
    clientId: 'client-dev-1',
    company: 'Northwind Labs',
    type: 'Onboarding',
    target: 'all',
    code: 'dev-preview'
  }
];

// --- Terms & Notes ---

export const termsAndConditionsHtml = `
<div style="font-family: var(--font-family-sans); color: var(--text-primary); max-width: 1024px; text-align: left;">
  <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--text-primary); text-align: left;">1. Canceled or Missed Sessions</h3>
  <ul style="margin-bottom: 24px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); padding-left: 20px; list-style-type: disc; text-align: left;">
    <li style="margin-bottom: 8px;"><strong>1:1 Sessions</strong>: Please allow at least 48 hours before canceling a session to be rescheduled. Sessions canceled within 48 hours of the upcoming session may not be rescheduled.</li>
    <li style="margin-bottom: 8px;"><strong>Group Sessions</strong>: Group sessions cannot be canceled unless the mentor or coach is unavailable.</li>
    <li><strong>No shows</strong>: These sessions will not be rescheduled. If more than 2 no shows, the cohort will end.</li>
  </ul>
  
  <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--text-primary); text-align: left;">2. Confidentiality</h3>
  <p style="margin-bottom: 24px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); text-align: left;">All communications with your match are confidential, but BestHuman/Locelle may reach out to ask for testimonials to be shared for marketing purposes.</p>

  <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--text-primary); text-align: left;">3. Satisfaction</h3>
  <ul style="margin-bottom: 24px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); padding-left: 20px; list-style-type: disc; text-align: left;">
    <li style="margin-bottom: 8px;"><strong>1:1</strong>: If you are not satisfied with your match, please let your Client Success team member know.</li>
    <li><strong>Group</strong>: If you are not satisfied with the group, we will cancel your plan and refund for all remaining sessions.</li>
  </ul>

  <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--text-primary); text-align: left;">4. Liability</h3>
  <p style="margin-bottom: 16px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); text-align: left;">Locelle, the mentors, coaches and/or your employer is not liable or responsible for any decisions you make as a result of participating in the program.</p>
  <p style="margin-bottom: 24px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); text-align: left;">As a participant, you consent to confidentiality and non-disclosure regarding sessions facilitated by Locelle and all its programs.</p>

  <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--text-primary); text-align: left;">5. Refund or Cancelation</h3>
  <ul style="margin-bottom: 24px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); padding-left: 20px; list-style-type: disc; text-align: left;">
    <li style="margin-bottom: 8px;">After onboarding, if you have not yet been matched or started a program, you may qualify for a refund minus a $100 admin fee.</li>
    <li style="margin-bottom: 8px;">If matched, you may qualify for a refund of $200.</li>
    <li>No refunds for sessions attended.</li>
  </ul>

  <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--text-primary); text-align: left;">6. Privacy Policy</h3>
  <p style="margin-bottom: 16px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); text-align: left;">If you choose to use our Service, then you agree to the collection and use of information in relation to this policy.</p>
</div>
`;

export function defaultNotesTemplate(participantCount: number): string {
  return participantCount === 1
    ? '<h2>One-on-One Session Notes</h2><p>Capture goals, themes, and next steps here.</p>'
    : '<h2>Group Session Notes</h2><p>Capture the main discussion, themes, and follow-ups here.</p>';
}
